import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { TripService, Trip } from '../../core/services/trip.service';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';

@Component({
    selector: 'app-trip-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        MatSnackBarModule,
        MatSelectModule,
        MatOptionModule
    ],
    template: `
    <div class="main-container">
      <div class="header-section">
          <h2>My Trips</h2>
          <button mat-mini-fab color="primary" (click)="toggleForm()">
              <mat-icon>{{ showForm ? 'close' : 'add' }}</mat-icon>
          </button>
      </div>

      <div class="scrollable-content">
          <div class="content-wrapper">
              <!-- Add/Edit Trip Form -->
              <mat-card class="form-card" *ngIf="showForm">
                <mat-card-header>
                    <mat-card-title>{{ isEditMode ? 'Edit Trip' : 'Create New Trip' }}</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                   <div class="form-column">
                     <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Trip Name</mat-label>
                        <input matInput [(ngModel)]="tripForm.name" placeholder="e.g. Goa Vacation">
                     </mat-form-field>
                     
                     <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Description</mat-label>
                        <textarea matInput [(ngModel)]="tripForm.description" placeholder="Notes..."></textarea>
                     </mat-form-field>

                     <div class="dates-row">
                        <mat-form-field appearance="outline" class="half-width">
                            <mat-label>Start Date</mat-label>
                            <input matInput [matDatepicker]="startPicker" [(ngModel)]="tripForm.startDate" required>
                            <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                            <mat-datepicker #startPicker></mat-datepicker>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="half-width">
                            <mat-label>End Date</mat-label>
                            <input matInput [matDatepicker]="endPicker" [(ngModel)]="tripForm.endDate" [min]="tripForm.startDate" required>
                            <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                            <mat-datepicker #endPicker></mat-datepicker>
                        </mat-form-field>
                     </div>

                     <div class="status-row" *ngIf="isEditMode">
                        <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Status</mat-label>
                            <mat-select [(ngModel)]="tripForm.status">
                                <mat-option value="active">Active</mat-option>
                                <mat-option value="completed">Completed</mat-option>
                            </mat-select>
                        </mat-form-field>
                     </div>
        
                     <div class="action-row">
                         <button mat-button (click)="resetForm()">Cancel</button>
                         <button mat-raised-button color="primary" 
                            [disabled]="!tripForm.name || !tripForm.startDate || !tripForm.endDate" 
                            (click)="saveTrip()">
                            {{ isEditMode ? 'Update Trip' : 'Create Trip' }}
                         </button>
                     </div>
                   </div>
                </mat-card-content>
              </mat-card>
        
              <!-- Trip List -->
              <div class="trip-list">
                 <mat-card *ngFor="let trip of tripService.trips$ | async" 
                           class="trip-card" 
                           [class.active-trip]="trip.status === 'active'"
                           [class.completed]="trip.status === 'completed'"
                           (click)="openTrip(trip)">
                    <mat-card-content>
                        <div class="card-body">
                            <!-- Row 1: Header + Actions -->
                            <div class="card-header-row">
                                <div class="title-section">
                                    <h3>{{ trip.name }}</h3>
                                    <p class="desc" *ngIf="trip.description">{{ trip.description }}</p>
                                </div>
                                <div class="actions">
                                     <button mat-icon-button color="primary" (click)="editTrip(trip, $event)">
                                        <mat-icon>edit</mat-icon>
                                    </button>
                                     <button mat-icon-button color="warn" (click)="deleteTrip(trip, $event)" 
                                        [disabled]="trip.status === 'completed'">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                    <mat-icon class="chevron" color="primary">chevron_right</mat-icon>
                                </div>
                            </div>

                            <!-- Row 2: Badges (Date & Status) -->
                            <div class="meta-row">
                                <span class="status-badge" [class.active]="trip.status === 'active'">
                                    {{ trip.status }}
                                </span>
                                <span class="date-badge" *ngIf="trip.startDate">
                                    <mat-icon>event</mat-icon>
                                    {{ trip.startDate | date:'mediumDate' }} 
                                    <span *ngIf="trip.endDate"> - {{ trip.endDate | date:'mediumDate' }}</span>
                                </span>
                            </div>
                        </div>
                    </mat-card-content>
                 </mat-card>
                 
                 <div class="empty-state" *ngIf="(tripService.trips$ | async)?.length === 0">
                    <p>No trips found. Create one to get started!</p>
                 </div>
              </div>
          </div>
      </div>
    </div>
  `,
    styles: [`
    :host {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background-color: var(--bg-color);
    }
    .main-container {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .header-section {
        flex: 0 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 24px;
        background-color: var(--bg-color);
        z-index: 10;
    }
    .scrollable-content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 100px; /* FIX: Clear Bottom Nav */
        overscroll-behavior: contain;
    }
    .content-wrapper {
        padding: 0 20px;
        max-width: 800px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
    h2 { 
        margin: 0; 
        color: var(--text-main); 
        font-family: 'Quicksand', sans-serif;
        font-weight: 700;
        font-size: 1.5rem;
    }
    
    .form-card {
        margin-bottom: 24px;
        animation: slideDown 0.3s ease-out;
        border-radius: 24px !important;
        box-shadow: var(--card-shadow) !important;
        border: none !important;
    }

    .form-card mat-card-title {
        font-family: 'Quicksand', sans-serif;
        font-weight: 700;
        font-size: 1.2rem;
    }
    
    .full-width { width: 100%; margin-bottom: 8px; }
    .half-width { width: 48%; }

    .form-column {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 8px;
    }
    
    .dates-row {
        display: flex;
        justify-content: space-between;
        gap: 10px;
    }

    .action-row {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }
    
    /* Trip List */
    .trip-list {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding-bottom: 20px;
    }

    .trip-card {
        margin-bottom: 0;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
        border-radius: 24px !important;
        box-shadow: 0 4px 20px rgba(0,0,0,0.04) !important;
        background: #fff;
        border: 1px solid rgba(0,0,0,0.03) !important;
        position: relative;
        overflow: hidden;
    }
    
    .trip-card:hover { 
        transform: translateY(-4px); 
        box-shadow: 0 12px 28px rgba(0,0,0,0.08) !important;
    }
    .trip-card:active { transform: scale(0.98); }
    
    .trip-card mat-card-content {
        padding: 20px 24px;
        position: relative;
        z-index: 2;
        display: block; /* content is now block, not flex-row */
    }

    /* Active Trip Indicator */
    .trip-card.active-trip::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 6px;
        background: linear-gradient(180deg, #6C5DD3 0%, #8A76FF 100%);
        opacity: 1;
    }

    .card-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .card-header-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
    }

    .title-section {
        flex: 1;
        min-width: 0;
    }

    .title-section h3 { 
        margin: 0 0 6px; 
        font-weight: 700; 
        font-size: 1.2rem;
        color: var(--text-main);
        letter-spacing: -0.01em;
        line-height: 1.3;
    }
    .title-section .desc { 
        margin: 0; 
        color: var(--text-soft); 
        font-size: 0.9rem; 
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .actions {
        display: flex;
        align-items: flex-start; /* Align to top */
        gap: 0;
        margin-top: -8px; /* Slight visual adjustment to align icons with text baseline */
        margin-right: -12px; /* Pull right to edge */
    }
    
    .actions button { 
        opacity: 0.6; 
        transform: scale(0.9);
    }
    .actions button:hover { opacity: 1; background: rgba(0,0,0,0.04); }
    
    .actions .chevron {
        opacity: 0.4;
        margin-left: 4px;
        margin-top: 8px; /* Align chevron visually */
    }

    .meta-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 4px;
    }
    /* Badges styles remain similar but ensuring they sit nicely */
    .status-badge {
        font-size: 0.7rem;
        padding: 4px 10px;
        border-radius: 6px; /* Squircle */
        background: #EEEEEE;
        color: #9E9E9E;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.5px;
    }
    .status-badge.active {
        background: rgba(108, 93, 211, 0.1);
        color: #6C5DD3; 
    }
    
    .date-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.85rem;
        color: #6E7C87;
        background: transparent;
        padding: 0;
        font-weight: 500;
    }

    /* Completed Trip Style */
    .trip-card.completed {
        opacity: 0.9;
        background: #FAFAFA;
        box-shadow: none !important;
        border: 1px solid rgba(0,0,0,0.05) !important;
    }
    .trip-card.completed h3 { 
        color: #9E9E9E; 
        text-decoration: line-through; 
    }

    /* Empty State */
    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: var(--text-soft);
        background: #fff;
        border-radius: 24px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.02);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

    @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
  `]
})
export class TripListComponent {
    tripService = inject(TripService);
    router = inject(Router);
    dialog = inject(MatDialog);
    snackBar = inject(MatSnackBar);

    showForm = false;
    isEditMode = false;
    editingTripId: string | null = null;

    tripForm = {
        name: '',
        description: '',
        startDate: null as Date | null,
        endDate: null as Date | null,
        status: 'active' as 'active' | 'completed'
    };

    toggleForm() {
        if (this.showForm && this.isEditMode) {
            // If already open in edit mode, reset to create mode or close?
            // User likely wants to close.
            this.resetForm();
        } else {
            this.showForm = !this.showForm;
            if (!this.showForm) this.resetForm();
        }
    }

    resetForm() {
        this.showForm = false;
        this.isEditMode = false;
        this.editingTripId = null;
        this.tripForm = {
            name: '',
            description: '',
            startDate: null,
            endDate: null,
            status: 'active'
        };
    }

    async saveTrip() {
        if (!this.tripForm.name) {
            this.showToast('Error', 'Trip Name is required', 'error');
            return;
        }

        if (!this.tripForm.startDate || !this.tripForm.endDate) {
            this.showToast('Error', 'Start and End dates are required', 'error');
            return;
        }

        const tripData: any = {
            name: this.tripForm.name,
            description: this.tripForm.description,
            startDate: this.tripForm.startDate.toISOString(),
            endDate: this.tripForm.endDate.toISOString()
        };

        if (this.isEditMode && this.editingTripId) {
            tripData.status = this.tripForm.status;
            await this.tripService.updateTrip(this.editingTripId, tripData);
            this.showToast('Success', 'Trip updated successfully', 'success');
        } else {
            await this.tripService.addTrip(tripData);
            this.showToast('Success', 'Trip created successfully', 'success');
        }

        this.resetForm();
    }

    editTrip(trip: Trip, event: Event) {
        event.stopPropagation();
        this.isEditMode = true;
        this.showForm = true;
        this.editingTripId = trip.id!;
        this.tripForm = {
            name: trip.name,
            description: trip.description || '',
            startDate: trip.startDate ? new Date(trip.startDate) : null,
            endDate: trip.endDate ? new Date(trip.endDate) : null,
            status: trip.status
        };
        // Scroll to top to see form
        document.querySelector('.scrollable-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    openTrip(trip: Trip) {
        this.router.navigate(['/trips', trip.id]);
    }

    deleteTrip(trip: Trip, event: Event) {
        event.stopPropagation();

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            panelClass: 'k-bottom-sheet-dialog',
            position: { bottom: '0' },
            data: {
                title: 'Delete Trip',
                icon: '✈️',
                value: trip.name,
                message: `Are you sure you want to delete "${trip.name}"?`,
                warning: 'All expenses and split-dues for this trip will be permanently removed.',
                confirmText: 'Delete Trip',
                type: 'delete'
            }
        });

        dialogRef.afterClosed().subscribe(async result => {
            if (result) {
                await this.tripService.deleteTrip(trip.id!);
                this.showToast('Success', 'Trip deleted successfully', 'success');
            }
        });
    }

    private showToast(title: string, message: string, type: 'success' | 'error') {
        this.snackBar.openFromComponent(ToastComponent, {
            data: { title, message, type },
            duration: 3000,
            panelClass: 'custom-toast-container',
            horizontalPosition: 'end',
            verticalPosition: 'bottom'
        });
    }
}
